/**
 * Upgrade and Currency Constants
 *
 * Defines all constants for the gold currency system, Score Boost upgrade,
 * and Combo Shield consumable items.
 */

import type { ComboShieldDefinition, UpgradeDefinition } from './types';

/**
 * Score Boost Permanent Upgrade
 *
 * 10 levels with increasing costs and cumulative score multiplier boost.
 * Total cost to max: 7,500 gold
 * Max effect: +50% permanent score multiplier
 */
export const SCORE_BOOST: UpgradeDefinition = {
  id: 'scoreBoost',
  name: 'Score Boost',
  description: 'Permanently increases all score earned',
  maxLevel: 10,
  costs: [50, 100, 150, 250, 400, 600, 850, 1200, 1600, 2300],
  cumulativeBoost: [0, 0.02, 0.05, 0.08, 0.12, 0.16, 0.21, 0.26, 0.32, 0.39, 0.5],
};

/**
 * Combo Shield Consumable Item
 *
 * Purchasable shields that protect combo from breaking on wrong taps.
 * Can hold up to 9 shields, but only use max 3 per game.
 */
export const COMBO_SHIELD: ComboShieldDefinition = {
  costPerShield: 50,
  bulkCost: 120,
  bulkAmount: 3,
  maxShields: 9,
  maxPerGame: 3,
};

/**
 * Gold Currency Constants
 */
export const GOLD_CONSTANTS = {
  BASE_GOLD: 10,
  GOLD_PER_SCORE_THRESHOLD: 2000,
  GOLD_PER_COMBO_THRESHOLD: 15,
  PERSONAL_BEST_BONUS: 25,

  GOLD_PER_AD: 200,
  AD_COOLDOWN_MINUTES: 5,
} as const;

/**
 * Get the cumulative score boost multiplier for a given level
 */
export const getScoreBoostMultiplier = (level: number): number => {
  if (level < 0) return 0;
  if (level >= SCORE_BOOST.cumulativeBoost.length) {
    return SCORE_BOOST.cumulativeBoost[SCORE_BOOST.cumulativeBoost.length - 1] as number;
  }
  return SCORE_BOOST.cumulativeBoost[level] as number;
};

/**
 * Get the cost to upgrade to the next score boost level
 */
export const getScoreBoostUpgradeCost = (currentLevel: number): number => {
  if (currentLevel < 0 || currentLevel >= SCORE_BOOST.maxLevel) {
    return -1;
  }
  return SCORE_BOOST.costs[currentLevel] as number;
};

/**
 * Calculate how many shields can be activated for a game
 */
export const getMaxActivatableShields = (shieldsOwned: number): number => {
  return Math.min(shieldsOwned, COMBO_SHIELD.maxPerGame);
};

/**
 * Calculate gold earned from a game session
 */
export const calculateGameGold = (
  score: number,
  maxCombo: number,
  isPersonalBest: boolean,
): {
  baseGold: number;
  scoreBonus: number;
  comboBonus: number;
  personalBestBonus: number;
  totalGold: number;
} => {
  const baseGold = GOLD_CONSTANTS.BASE_GOLD;
  const scoreBonus = Math.floor(score / GOLD_CONSTANTS.GOLD_PER_SCORE_THRESHOLD);
  const comboBonus = Math.floor(maxCombo / GOLD_CONSTANTS.GOLD_PER_COMBO_THRESHOLD);
  const personalBestBonus = isPersonalBest ? GOLD_CONSTANTS.PERSONAL_BEST_BONUS : 0;

  return {
    baseGold,
    scoreBonus,
    comboBonus,
    personalBestBonus,
    totalGold: baseGold + scoreBonus + comboBonus + personalBestBonus,
  };
};
